import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Legend,
} from "recharts";
import { CircularProgress } from "@mui/material";
import {
  ParentGroupsAlertData,
  ParentGroupsAlertDataLabel,
} from "./AnalyticsUtils/analyticsInterfaces";
import { NavigateFunction } from "react-router-dom";
import { handleLevel1IndexClick } from "../Sites/SiteAboutPage/SiteAboutPageUtils/handleLevel1IndexClick";
import { createColor } from "./AnalyticsUtils/createColor";
import { useAnalyticsStore } from "./AnalyticsStore/analyticsStore";
import { getFullArray } from "./AnalyticsUtils/getFullArray";

export const AlertsByParentGroupsGraph = ({
  navigate,
  navBarCollapsed,
}: {
  navigate: NavigateFunction;
  navBarCollapsed: boolean;
}) => {
  const [outerBoxHeight, setOuterBoxHeight] = React.useState(425);
  const [innerBoxHeight, setInnerBoxHeight] = React.useState(423);

  const {
    parentGroupsAlertsData,
    conversionArray,
    loadingData,
    alertGraphName,
    setDisplayOnLeftBool,
    setShowMoreOpen,
    setVisibleArray,
    showMoreVisible,
  } = useAnalyticsStore();

  const optionIds = new Map();

  parentGroupsAlertsData.forEach((object: ParentGroupsAlertData) => {
    optionIds.set(object.name, object.id);
  });

  const labelNames: string[] = [];
  const labelDisplayNames: string[] = [];
  const labelsArray: { label: ParentGroupsAlertDataLabel }[] = [];
  let labelStringLength = 0;
  parentGroupsAlertsData.forEach((object: ParentGroupsAlertData) => {
    if (object.label && object.label.display_name) {
      if (!labelDisplayNames.includes(object.label.display_name)) {
        labelDisplayNames.push(object.label.display_name);
        labelStringLength += object.label.display_name.length;
      }
    }
    if (!labelsArray[0]) {
      labelNames.push(object.label.name);
      labelsArray.push({
        label: object.label,
      });
    }
    labelsArray.forEach(() => {
      if (!labelNames.includes(object.label.name)) {
        labelsArray.push({
          label: object.label,
        });
        labelNames.push(object.label.name);
      }
    });
  });

  let bottomLength = 56;

  if (labelStringLength > 47 && labelStringLength < 95) {
    bottomLength = 76;
  }

  if (labelStringLength > 94) {
    bottomLength = 96;
  }

  const fullArray = React.useMemo(
    () => getFullArray(conversionArray),
    [conversionArray]
  );

  React.useEffect(() => {
    setDisplayOnLeftBool(false);
    setShowMoreOpen(false);
    setVisibleArray(fullArray);
    setOuterBoxHeight(425);
    setInnerBoxHeight(423);
  }, [fullArray, setDisplayOnLeftBool, setShowMoreOpen, setVisibleArray]);

  return (
    <div
      style={
        navBarCollapsed
          ? {
              position: "absolute",
              top: 70,
              left: 690,
              right: 1,
              height: outerBoxHeight,
              width: 656,
              backgroundColor: "#BDBDBD",
            }
          : {
              position: "absolute",
              top: 70,
              left: 650,
              right: 1,
              height: outerBoxHeight,
              width: 620,
              backgroundColor: "#BDBDBD",
            }
      }
    >
      <div
        style={{
          position: "absolute",
          top: 1,
          left: 1,
          right: 1,
          height: innerBoxHeight,
          background: "white",
        }}
      ></div>
      <p className="text-actuate-blue font-mulish text-[22px] font-bold absolute top-[8px] left-[40px]">
        {`Alerts by ${alertGraphName}`}
      </p>
      <p className="text-actuate-blue font-mulish text-[14px] absolute top-[36px] left-[40px]">
        {`Total alerts for each ${alertGraphName} during the selected dates`}
      </p>
      {showMoreVisible && (
        <div
          className={
            !navBarCollapsed
              ? "absolute top-[5px] left-[480px]"
              : "absolute top-[5px] left-[516px]"
          }
        >
          {/* <ShowMoreButton fullArray={fullArray} max7Array={max7Array} /> */}
        </div>
      )}

      {!loadingData.parentGroupsData && (
        <div className="absolute top-[60px] left-[0px] h-[290px] overflow-scroll scrollbar-hide z-10">
          <BarChart
            layout="vertical"
            width={!navBarCollapsed ? 620 : 656}
            height={fullArray.length > 3 ? Number(fullArray.length) * 66 : 290}
            data={fullArray}
            barSize={50}
            margin={{
              top: 5,
              right: 30,
              left: 50,
              bottom: 5,
            }}
            onClick={(event: any) => {
              if (alertGraphName === "Parent Group") {
                handleLevel1IndexClick(
                  optionIds.get(event.activePayload[0].payload.name),
                  navigate
                );
              }
              if (alertGraphName === "Site") {
                navigate(
                  `/sites/aboutsite/${optionIds.get(
                    event.activePayload[0].payload.name
                  )}`
                );
              }
              //  if (alertGraphName === "Camera"){
              //   getCameraGeneralInfo(optionIds.get(event.activePayload[0].payload.name), navigate)
              //  }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} />
            <YAxis dataKey="name" type="category" hide />
            {fullArray[0] && <Tooltip />}
            <Legend height={bottomLength} />

            {labelsArray.map((object, index: number) => {
              if (index === 0) {
                return (
                  <Bar
                    cursor="pointer"
                    key={object.label.name}
                    dataKey={object.label.name}
                    stackId="a"
                    fill={createColor(object.label.name, object.label.color)}
                    name={
                      object.label.display_name.charAt(0).toUpperCase() +
                      object.label.display_name.slice(1)
                    }
                  >
                    <LabelList
                      dataKey="abreviated_name"
                      position="left"
                      width={220}
                    />
                  </Bar>
                );
              } else {
                return (
                  <Bar
                    cursor="pointer"
                    key={object.label.name}
                    dataKey={object.label.name}
                    stackId="a"
                    fill={createColor(object.label.name, object.label.color)}
                    name={
                      object.label.display_name.charAt(0).toUpperCase() +
                      object.label.display_name.slice(1)
                    }
                  ></Bar>
                );
              }
            })}
          </BarChart>
        </div>
      )}
      {loadingData.parentGroupsData && (
        <div className="absolute top-[170px] left-[230px]">
          <CircularProgress size={25} sx={{ position: "relative", left: 40 }} />
          <div className="font-mulish text-actuate-blue test-bold">
            Fetching Data...
          </div>
        </div>
      )}
    </div>
  );
};
